<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Solicitudes de Reembolso</h1>

      <v-col cols="12" md="6" class="my-0 py-0">
        <v-container class="ma-0 pa-0 mt-3 mt-md-0 d-flex align-end justify-end" style="gap: 8px;">
          <!-- Input de archivo oculto -->
          <input
            ref="paymentFileInput"
            type="file"
            accept=".xlsx,.xls,.csv"
            style="display: none;"
            @change="handlePaymentFile"
          />
          <v-btn
            v-if="can('refund-inbox:import-payments')"
            color="#3770FA"
            depressed
            @click="paymentIntroDialog = true"
          >
            <v-icon color="white">mdi-upload</v-icon>
            <span class="white--text font-weight-semibold ml-1">Cargar pagos</span>
          </v-btn>
          <v-btn
            v-if="can('refund-inbox:export-payroll')"
            color="#5E35B1"
            depressed
            @click="openPayrollDialog"
          >
            <v-icon color="white">mdi-file-download</v-icon>
            <span class="white--text font-weight-semibold ml-1">Nómina</span>
          </v-btn>
          <v-btn color="#1CA762" depressed @click="refresh()">
            <v-icon color="white"> mdi-refresh </v-icon>
            <span class="white--text font-weight-semibold">Actualizar</span>
          </v-btn>
        </v-container>
      </v-col>
    </v-row>

    <!-- Diálogo previo a la carga: qué hace, qué archivo espera y qué no procesa -->
    <v-dialog v-model="paymentIntroDialog" max-width="620">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">
          <v-icon left color="#3770FA">mdi-cash-multiple</v-icon>
          Cargar pagos
        </v-card-title>
        <v-card-text>
          <p class="mb-3" style="color: #555;">
            Sube el archivo que devuelve el banco para marcar como
            <strong>Pagados</strong> los reembolsos transferidos.
          </p>

          <div class="text-subtitle-2 font-weight-bold mb-1">El archivo debe tener</div>
          <ul class="mb-3" style="font-size: 13px; color: #555;">
            <li>Formato <code>.xlsx</code>, <code>.xls</code> o <code>.csv</code>, con cabeceras.</li>
            <li>
              Una columna con el <strong>n° de solicitud</strong> (sirve "solicitud", "reembolso",
              "nro", "folio"…).
            </li>
            <li>
              Una columna con la <strong>fecha de pago</strong> (formatos
              <code>DD/MM/AAAA</code>, <code>DD-MM-AAAA</code>, <code>DD.MM.AAAA</code> o <code>AAAA-MM-DD</code>).
            </li>
          </ul>
          <v-btn text small color="#3770FA" class="mb-3" style="margin-left: -8px;" @click="downloadPaymentsTemplate">
            <v-icon small left>mdi-download</v-icon>Descargar plantilla (.xlsx)
          </v-btn>

          <v-alert type="warning" dense outlined class="mb-2" icon="mdi-cash-remove">
            <div class="font-weight-bold mb-1">Los "Pago inválido" no se procesan</div>
            <div style="font-size: 12px;">
              Si el banco rechazó una transferencia y marcaste el reembolso como
              <strong>Pago inválido</strong>, esta carga lo va a omitir y te lo va a informar al
              final. Corrige los datos bancarios y pásalo a <strong>Pagado</strong> a mano desde el
              detalle de la solicitud.
            </div>
          </v-alert>

          <div style="font-size: 12px; color: #777;">
            Solo se marcan los reembolsos en estado <strong>Aprobado</strong>. Los ya pagados, los
            rechazados y los que no existan se informan aparte, sin tocarlos. Antes de escribir nada
            vas a ver un resumen para confirmar.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="paymentIntroDialog = false">Cancelar</v-btn>
          <v-btn color="#3770FA" dark depressed :loading="loadingPayments" @click="triggerFileUpload">
            <v-icon left>mdi-upload</v-icon>
            Seleccionar archivo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de carga de pagos — 2 pasos: confirmación → resultado -->
    <v-dialog v-model="paymentDialog" max-width="580" @click:outside="closePaymentDialog">
      <v-card>
        <!-- ── PASO 1: Confirmación ── -->
        <template v-if="!paymentResults">
          <v-card-title class="text-h6 font-weight-bold">
            <v-icon left color="#3770FA">mdi-cash-multiple</v-icon>
            Confirmar carga de pagos
          </v-card-title>
          <v-card-text>
            <p class="mb-2">
              Se detectaron <strong>{{ parsedPayments.length }}</strong> reembolsos en
              <strong>{{ parsedPaymentGroups.length }}</strong> fecha(s) distintas.
            </p>
            <v-alert v-if="paymentParseError" type="error" dense class="mb-2">
              {{ paymentParseError }}
            </v-alert>
            <v-simple-table dense v-if="parsedPaymentGroups.length">
              <thead>
                <tr>
                  <th>Fecha de pago</th>
                  <th style="text-align:center">Reembolsos</th>
                  <th>Nros. de solicitud</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="group in parsedPaymentGroups" :key="group.fecha">
                  <td>{{ group.fecha }}</td>
                  <td style="text-align:center">{{ group.refunds.length }}</td>
                  <td style="font-size: 11px; color: #555;">
                    {{ group.refunds.slice(0, 5).join(', ')
                    }}{{ group.refunds.length > 5 ? ` ...+${group.refunds.length - 5}` : '' }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="closePaymentDialog" :disabled="submittingPayments">Cancelar</v-btn>
            <v-btn
              color="#3770FA"
              dark
              depressed
              :loading="submittingPayments"
              :disabled="!parsedPaymentGroups.length"
              @click="submitPayments"
            >
              Confirmar
            </v-btn>
          </v-card-actions>
        </template>

        <!-- ── PASO 2: Resultado ── -->
        <template v-else>
          <v-card-title class="text-h6 font-weight-bold">
            <v-icon left color="#1CA762">mdi-check-circle-outline</v-icon>
            Resultado de la carga
          </v-card-title>
          <v-card-text>
            <!-- Procesados con éxito -->
            <v-alert type="success" dense outlined class="mb-3" icon="mdi-cash-check">
              <strong>{{ paymentResults.processed }}</strong>
              {{ paymentResults.processed === 1 ? 'reembolso marcado' : 'reembolsos marcados' }}
              como <strong>Pagado</strong>
            </v-alert>

            <!-- Grupo: No tienen status aprobado -->
            <v-alert
              v-if="paymentResults.notApproved.length"
              type="warning"
              dense
              outlined
              class="mb-2"
              icon="mdi-clock-alert-outline"
            >
              <div class="font-weight-bold mb-2">
                No tienen status aprobado ({{ paymentResults.notApproved.length }})
              </div>
              <div
                v-for="group in notApprovedGrouped"
                :key="group.label"
                style="font-size:12px; color:#555; margin-bottom:3px;"
              >
                <span style="font-weight:600;">{{ group.label }}:</span>
                {{ group.refunds.join(', ') }}
              </div>
            </v-alert>

            <!-- Grupo: Pago inválido — requieren paso manual -->
            <v-alert
              v-if="paymentResults.invalidPayment && paymentResults.invalidPayment.length"
              type="warning"
              dense
              outlined
              class="mb-2"
              icon="mdi-cash-remove"
            >
              <div class="font-weight-bold mb-1">
                Con pago inválido ({{ paymentResults.invalidPayment.length }})
              </div>
              <div style="font-size:12px; color:#555;">
                {{ paymentResults.invalidPayment.join(', ') }}
              </div>
              <div style="font-size:11px; color:#777; margin-top:4px;">
                Corrige los datos bancarios y pásalos a Pagado a mano desde el detalle.
              </div>
            </v-alert>

            <!-- Grupo: Ya fueron pagados -->
            <v-alert
              v-if="paymentResults.alreadyPaid.length"
              type="info"
              dense
              outlined
              class="mb-2"
              icon="mdi-check-all"
            >
              <div class="font-weight-bold mb-1">
                Ya fueron pagados ({{ paymentResults.alreadyPaid.length }})
              </div>
              <div style="font-size:12px; color:#555;">
                {{ paymentResults.alreadyPaid.join(', ') }}
              </div>
            </v-alert>

            <!-- Grupo: No encontrados -->
            <v-alert
              v-if="paymentResults.notFound.length"
              type="warning"
              dense
              outlined
              class="mb-2"
              icon="mdi-magnify-remove-outline"
            >
              <div class="font-weight-bold mb-1">
                No encontrados ({{ paymentResults.notFound.length }})
              </div>
              <div style="font-size:12px; color:#555;">
                {{ paymentResults.notFound.join(', ') }}
              </div>
            </v-alert>

            <!-- Grupo: Otro error -->
            <v-alert
              v-if="paymentResults.failed.length"
              type="error"
              dense
              outlined
              class="mb-2"
              icon="mdi-alert-circle-outline"
            >
              <div class="font-weight-bold mb-1">
                Otro error ({{ paymentResults.failed.length }})
              </div>
              <div style="font-size:12px; color:#555;">
                {{ paymentResults.failed.join(', ') }}
              </div>
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="#3770FA" dark depressed @click="closePaymentDialog">Cerrar</v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <!-- Diálogo de nómina — 2 pasos: elección de país → resultado -->
    <v-dialog v-model="payrollDialog" max-width="640" @click:outside="closePayrollDialog">
      <!-- ── PASO 1: Elección de nómina ── -->
      <v-card v-if="!payroll">
        <v-card-title class="text-h6 font-weight-bold">
          <v-icon left color="#5E35B1">mdi-file-download</v-icon>
          Generar nómina de pagos
        </v-card-title>
        <v-card-text>
          <template v-if="!showClFormatChoice">
            <p class="mb-4" style="color: #555;">
              Incluye todos los reembolsos <strong>aprobados y aún no pagados</strong> del país que
              elijas.
            </p>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-card
                  outlined
                  hover
                  class="pa-4 text-center"
                  :disabled="loadingPayroll"
                  @click="showClFormatChoice = true"
                >
                  <v-icon size="36" color="#5E35B1">mdi-file-delimited-outline</v-icon>
                  <div class="text-subtitle-1 font-weight-bold mt-2">Chile</div>
                  <div style="font-size: 12px; color: #777;">Archivo CSV o Excel para el banco</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card
                  outlined
                  hover
                  class="pa-4 text-center"
                  :disabled="loadingPayroll"
                  @click="generatePayroll('PE')"
                >
                  <v-icon size="36" color="#5E35B1">mdi-text-box-outline</v-icon>
                  <div class="text-subtitle-1 font-weight-bold mt-2">Perú</div>
                  <div style="font-size: 12px; color: #777;">Texto para copiar y pegar</div>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <!-- Chile tiene dos formatos de nómina: el CSV histórico para BICE
               y la plantilla Excel de Global66. -->
          <template v-else>
            <p class="mb-4" style="color: #555;">
              Elige el formato de la nómina para <strong>Chile</strong>.
            </p>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-card
                  outlined
                  hover
                  class="pa-4 text-center"
                  :disabled="loadingPayroll"
                  @click="generatePayroll('CL', 'bice')"
                >
                  <v-icon size="36" color="#5E35B1">mdi-bank-outline</v-icon>
                  <div class="text-subtitle-1 font-weight-bold mt-2">Bice</div>
                  <div style="font-size: 12px; color: #777;">Archivo CSV para el banco</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card
                  outlined
                  hover
                  class="pa-4 text-center"
                  :disabled="loadingPayroll"
                  @click="generatePayroll('CL', 'global66')"
                >
                  <v-icon size="36" color="#5E35B1">mdi-file-excel-outline</v-icon>
                  <div class="text-subtitle-1 font-weight-bold mt-2">Global66</div>
                  <div style="font-size: 12px; color: #777;">Excel (.xlsm) para Global66</div>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <v-progress-linear
            v-if="loadingPayroll"
            indeterminate
            color="#5E35B1"
            class="mt-4"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="showClFormatChoice"
            text
            :disabled="loadingPayroll"
            @click="showClFormatChoice = false"
          >
            <v-icon left small>mdi-arrow-left</v-icon>
            Atrás
          </v-btn>
          <v-spacer />
          <v-btn text :disabled="loadingPayroll" @click="closePayrollDialog">Cancelar</v-btn>
        </v-card-actions>
      </v-card>

      <!-- ── PASO 2: Resultado ── -->
      <v-card v-else>
        <v-card-title class="text-h6 font-weight-bold">
          <v-icon left color="#5E35B1">mdi-file-download</v-icon>
          Nómina {{ payroll.country === 'CL' ? 'Chile' : 'Perú' }}
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            <strong>{{ payroll.included }}</strong>
            {{ payroll.included === 1 ? 'reembolso aprobado' : 'reembolsos aprobados' }} en la
            nómina.
          </p>

          <v-alert v-if="!payroll.included" type="info" dense class="mb-2">
            No hay reembolsos aprobados con datos completos para este país.
          </v-alert>

          <v-textarea
            v-if="payroll.format === 'text' && payroll.included"
            :value="payroll.content"
            readonly
            outlined
            dense
            rows="12"
            class="mb-2"
            style="font-family: monospace; font-size: 12px;"
          />

          <!-- Vista previa del CSV: el archivo no lleva cabeceras, así que la
               tabla las muestra solo en pantalla para poder leer las columnas. -->
          <template v-if="payroll.format === 'csv' && payroll.included">
            <div class="text-caption font-weight-bold mb-1" style="color: #555;">
              Vista previa
              <span v-if="payrollPreviewRows.length < payroll.included" style="font-weight: 400;">
                (primeras {{ payrollPreviewRows.length }} de {{ payroll.included }} filas)
              </span>
            </div>
            <div class="mb-2" style="overflow-x: auto; border: 1px solid #e0e0e0; border-radius: 4px;">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th v-for="header in payrollCsvHeaders" :key="header" style="white-space: nowrap;">
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in payrollPreviewRows" :key="index">
                    <td
                      v-for="(cell, cellIndex) in row"
                      :key="cellIndex"
                      style="font-family: monospace; font-size: 11px; white-space: nowrap;"
                    >
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </template>

          <!-- Vista previa de Global66: mismas filas que terminarán en el Excel. -->
          <template v-if="payroll.format === 'global66' && payroll.included">
            <div class="text-caption font-weight-bold mb-1" style="color: #555;">
              Vista previa
              <span v-if="payrollGlobal66PreviewRows.length < payroll.included" style="font-weight: 400;">
                (primeras {{ payrollGlobal66PreviewRows.length }} de {{ payroll.included }} filas)
              </span>
            </div>
            <div class="mb-2" style="overflow-x: auto; border: 1px solid #e0e0e0; border-radius: 4px;">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th v-for="header in payrollGlobal66Headers" :key="header" style="white-space: nowrap;">
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in payrollGlobal66PreviewRows" :key="index">
                    <td
                      v-for="(cell, cellIndex) in row"
                      :key="cellIndex"
                      style="font-family: monospace; font-size: 11px; white-space: nowrap;"
                    >
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </template>

          <v-expansion-panels v-if="payroll.excluded.length" flat class="mb-2">
            <v-expansion-panel>
              <v-expansion-panel-header class="orange--text text--darken-3">
                Excluidos de la nómina ({{ payroll.excluded.length }})
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div
                  v-for="item in payroll.excluded"
                  :key="item.referenceIndex"
                  style="font-size: 12px; color: #555;"
                >
                  #{{ item.referenceIndex || '—' }} — {{ item.reason }}
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="payroll = null">
            <v-icon left small>mdi-arrow-left</v-icon>
            Volver
          </v-btn>
          <v-spacer />
          <v-btn text @click="closePayrollDialog">Cerrar</v-btn>
          <v-btn
            v-if="payroll.format === 'text' && payroll.included"
            color="#5E35B1"
            dark
            depressed
            @click="copyPayrollToClipboard"
          >
            Copiar
          </v-btn>
          <v-btn
            v-if="payroll.format === 'csv' && payroll.included"
            color="#5E35B1"
            dark
            depressed
            @click="downloadPayrollCsv"
          >
            Descargar CSV
          </v-btn>
          <v-btn
            v-if="payroll.format === 'global66' && payroll.included"
            color="#5E35B1"
            dark
            depressed
            :loading="loadingPayroll"
            @click="downloadGlobal66Excel"
          >
            Descargar Excel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar de éxito -->
    <v-snackbar v-model="paymentSuccess" color="#1CA762" timeout="4000" top>
      <v-icon left>mdi-check-circle</v-icon>
      {{ paymentSuccessMsg }}
    </v-snackbar>

    <!-- Snackbar de error -->
    <v-snackbar v-model="paymentError" color="red" timeout="5000" top>
      <v-icon left>mdi-alert-circle</v-icon>
      {{ paymentErrorMsg }}
    </v-snackbar>
    <v-data-table
      :headers="headers"
      :items="refundsRequests"
      :loading="loadingData"
      :items-per-page="limit"
      :server-items-length="totalItems"
      disable-sort
      :options.sync="options"
      class="elevation-1"
      :footer-props="{
        'items-per-page-options': [5, 10, 20, 30, 40, 50, 500, 999]
      }"
    >
      <template v-slot:header.referenceIndex="{ header }">
        <div @click="toggleSort('referenceIndex')" style="cursor: pointer; user-select: none;">
          {{ header.text }}
          <v-icon v-if="orderBy === 'referenceIndex' && orderDirection === 'desc'" small
            >mdi-arrow-down</v-icon
          >
          <v-icon v-if="orderBy === 'referenceIndex' && orderDirection === 'asc'" small
            >mdi-arrow-up</v-icon
          >
        </div>
      </template>
      <template v-slot:header.planName="{ header }">
        <div @click="toggleSort('planName')" style="cursor: pointer; user-select: none;">
          {{ header.text }}
          <v-icon v-if="orderBy === 'planName' && orderDirection === 'desc'" small
            >mdi-arrow-down</v-icon
          >
          <v-icon v-if="orderBy === 'planName' && orderDirection === 'asc'" small
            >mdi-arrow-up</v-icon
          >
        </div>
      </template>

      <template v-slot:header.updated="{ header }">
        <div @click="toggleSort('updatedAt')" style="cursor: pointer; user-select: none;">
          {{ header.text }}
          <v-icon v-if="orderBy === 'updatedAt' && orderDirection === 'desc'" small
            >mdi-arrow-down</v-icon
          >
          <v-icon v-if="orderBy === 'updatedAt' && orderDirection === 'asc'" small
            >mdi-arrow-up</v-icon
          >
        </div>
      </template>

      <template v-slot:header.daysRemaining="{ header }">
        <div @click="toggleSort('daysRemaining')" style="cursor: pointer; user-select: none;">
          {{ header.text }}
          <v-icon v-if="orderBy === 'daysRemaining' && orderDirection === 'desc'" small
            >mdi-arrow-down</v-icon
          >
          <v-icon v-if="orderBy === 'daysRemaining' && orderDirection === 'asc'" small
            >mdi-arrow-up</v-icon
          >
        </div>
      </template>
      <template v-slot:top>
        <v-col>
          <v-row class="px-5 py-5" style="justify-content: space-between; align-items: center">
            <v-col cols="12" md="6" class="my-0 py-0">
              <v-text-field
                v-model="searchRefund"
                :label="searchLabel"
                :disabled="loadingData"
                placeholder=""
                dense
                hide-details
                outlined
                append-icon="mdi-magnify"
                class="search-field"
                @click:append="searchFn"
                @keyup.enter="searchFn"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="my-0 py-0">
              <v-autocomplete
                v-model="supplierFilter"
                :items="availableSuppliers"
                :loading="loadingSuppliers"
                label="Cobertura"
                :disabled="loadingData"
                dense
                hide-details
                outlined
                clearable
                class="search-field"
                @change="onSupplierFilterChange"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row class="px-5 pt-5 pb-5 align-center">
            <v-chip-group v-model="pendingSearch" column multiple class="mx-3">
              <v-chip
                filter
                outlined
                v-for="(state, index) in states"
                v-longclick="() => longPressChip(index)"
                :key="state.value"
                :color="refundStatusColor(state.sequence)"
                :disabled="loadingData"
              >
                {{ state.text }}
              </v-chip>
            </v-chip-group>

            <v-btn
              small
              depressed
              color="#3770FA"
              class="mx-3"
              :dark="hasPendingFilterChanges && !loadingData"
              :disabled="!hasPendingFilterChanges || loadingData"
              @click="applyStatusFilter"
            >
              <v-icon left small>mdi-filter-check</v-icon>
              Aplicar filtro
            </v-btn>

            <span
              v-if="hasPendingFilterChanges"
              style="font-size: 12px; color: #CC7A00;"
            >
              Cambios sin aplicar
            </span>
          </v-row>
        </v-col>
      </template>

      <template v-slot:item.companyName="{ item }">
        <span>{{ item.companyName || '—' }}</span>
      </template>

      <template v-slot:item.planName="{ item }">
        <span>{{ item.planName || '—' }}</span>
      </template>

      <template v-slot:item.supplier="{ item }">
        <v-chip v-if="isThirdPartySupplier(item)" small outlined color="#5E35B1">
          {{ item.supplier.name }}
        </v-chip>
        <span v-else>—</span>
      </template>

      <template v-slot:item.user.identifier="{ item }">
        <span>{{ item.user ? item.user.identifier : '' }}</span>
      </template>

      <template v-slot:item.user="{ item }">
        <router-link
          v-if="item.user && item.user.id && can('users:view')"
          :to="`/users/${item.user.id}`"
          class="pw-cell-link"
        >{{ item.user.name }} {{ item.user.surname }}</router-link>
        <span v-else>{{ item.user ? item.user.name : '' }} {{ item.user ? item.user.surname : '' }}</span>
      </template>

      <template v-slot:item.pet="{ item }">
        <v-chip
          small
          :color="getMembershipColor(item.membershipStatus)"
          class="white--text font-weight-bold"
        >
          <v-icon left>{{ item.pet.species === 'dog' ? 'mdi-dog' : 'mdi-cat' }}</v-icon>
          {{ item.pet.name }}
        </v-chip>
      </template>

      <template v-slot:item.service="{ item }">
        <v-col class="ma-0 pa-0 mt-1" style="max-width: 200px;">
          <span>{{ item.service ? item.service.title : '-' }} </span>
        </v-col>
      </template>

      <template v-slot:item.updated="{ item }">
        <div>
          <span class="black--text font-weight-semibold">
            {{ parseDate(new Date(item.updatedAt)) }}
          </span>
        </div>
      </template>

      <template v-slot:item.changelogs="{ item }">
        <div v-if="item.changelogs !== null || item.changelogs.length > 0">
          <v-chip small outlined :color="item.changelogs.status.highlightColor">{{
            item.changelogs.status.name
          }}</v-chip>
        </div>
        <div v-else>
          <v-chip x-small outlined color="red" class="white--text font-weight-bold"
            >Sin información</v-chip
          >
        </div>
      </template>
      <template v-slot:item.dongraf="{ item }">
        <div class="d-flex justify-center align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                :style="{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: dongrafColor(item.dongraf)
                }"
              ></div>
            </template>
            <span>{{ dongrafTooltip(item.dongraf) }}</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:item.daysRemaining="{ item }">
        <v-chip
          v-if="getDaysRemaining(item) !== null"
          small
          :color="getDaysRemainingColor(getDaysRemaining(item))"
          class="white--text font-weight-bold"
        >
          {{ getDaysRemaining(item) }}
        </v-chip>
        <span v-else>—</span>
      </template>

      <template v-slot:item.serviceUsedAt="{ item }">
        <div
          v-if="editingCell && editingCell.id === item.id && editingCell.field === 'serviceUsedAt'"
          class="d-flex align-center"
          style="gap:4px;"
        >
          <v-text-field
            v-model="editingCell.value"
            type="date"
            dense
            outlined
            hide-details
            style="max-width:150px; font-size:12px;"
            @keydown.enter.prevent="saveQuickEdit(item)"
            @keyup.esc="editingCell = null"
            autofocus
          />
          <v-btn icon x-small color="primary" :loading="savingCell" @click="saveQuickEdit(item)"
            ><v-icon x-small>mdi-check</v-icon></v-btn
          >
          <v-btn icon x-small @click="editingCell = null"><v-icon x-small>mdi-close</v-icon></v-btn>
        </div>
        <span
          v-else-if="can('refund-inbox:update')"
          style="white-space:nowrap; cursor:pointer; border-bottom: 1px dashed #9CA3AF;"
          @click="startEdit(item, 'serviceUsedAt')"
          >{{ item.serviceUsedAt || '—' }}</span
        >
        <span v-else style="white-space:nowrap;">{{ item.serviceUsedAt || '—' }}</span>
      </template>
      <template v-slot:item.requestedAmount="{ item }">
        <div
          v-if="
            editingCell && editingCell.id === item.id && editingCell.field === 'requestedAmount'
          "
          class="d-flex align-center flex-nowrap amount-edit-cell"
        >
          <v-text-field
            v-model="editingCell.value"
            type="number"
            step="1"
            dense
            outlined
            hide-details
            prefix="$"
            class="amount-edit-field"
            @keydown="onAmountKeydown($event, item)"
            @keyup.enter="saveQuickEdit(item)"
            @keyup.esc="editingCell = null"
            autofocus
          />
          <v-btn icon x-small color="primary" :loading="savingCell" @click="saveQuickEdit(item)"
            ><v-icon x-small>mdi-check</v-icon></v-btn
          >
          <v-btn icon x-small @click="editingCell = null"><v-icon x-small>mdi-close</v-icon></v-btn>
        </div>
        <span
          v-else-if="can('refund-inbox:update')"
          style="white-space:nowrap; cursor:pointer; border-bottom: 1px dashed #9CA3AF;"
          @click="startEdit(item, 'requestedAmount')"
          >{{ formatAmount(item, 'requestedAmount') }}</span
        >
        <span v-else style="white-space:nowrap;">{{ formatAmount(item, 'requestedAmount') }}</span>
      </template>
      <template v-slot:item.approvedAmount="{ item }">
        <div
          v-if="editingCell && editingCell.id === item.id && editingCell.field === 'approvedAmount'"
          class="d-flex align-center flex-nowrap amount-edit-cell"
        >
          <v-text-field
            v-model="editingCell.value"
            type="number"
            :step="countryMoneyOf(item).decimals > 0 ? '0.01' : '1'"
            :max="getRefundAmountCap(item) ? getRefundAmountCap(item).value : undefined"
            dense
            outlined
            hide-details
            :prefix="countryMoneyOf(item).symbol"
            class="amount-edit-field"
            @keydown="onAmountKeydown($event, item)"
            @keyup.enter="saveQuickEdit(item)"
            @keyup.esc="editingCell = null"
            autofocus
          />
          <v-btn icon x-small color="primary" :loading="savingCell" @click="saveQuickEdit(item)"
            ><v-icon x-small>mdi-check</v-icon></v-btn
          >
          <v-btn icon x-small @click="editingCell = null"><v-icon x-small>mdi-close</v-icon></v-btn>
        </div>
        <span
          v-else-if="can('refund-inbox:update')"
          style="white-space:nowrap; cursor:pointer; border-bottom: 1px dashed #9CA3AF;"
          @click="startEdit(item, 'approvedAmount')"
          >{{ formatAmount(item, 'approvedAmount') }}</span
        >
        <span v-else style="white-space:nowrap;">{{ formatAmount(item, 'approvedAmount') }}</span>
      </template>
      <template v-slot:item.referenceIndex="{ item }">
        <div v-if="canSeeDetail && (item.changelogs !== null || item.changelogs.length > 0)">
          <refund-mainteiner
            :data="item"
            :refreshParent="refresh"
            :externalLabel="String(item.referenceIndex)"
            :auto-open="String(item.referenceIndex) === autoOpenRefund"
            @auto-opened="autoOpenRefund = null"
          />
        </div>
        <span v-else>{{ item.referenceIndex }}</span>
      </template>
    </v-data-table>
    <overlay :value="loadingOverlay"></overlay>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import RefundServices, { refundErrorMessage } from '../services/refunds.service';
import RefundMainteiner from '../components/RefundMainteiner';

// Índices de los chips de estado (refunds.service.getRefunds): Registrado … Pago inválido.
const ALL_REFUND_STATES = [0, 1, 2, 3, 4, 5, 6];

import Overlay from '../components/Overlay.vue';
import DateUtils from '../utils/date.utils';
import BusinessDaysUtils from '../utils/business-days.utils';
import CurrencyUtils from '../utils/currency.utils';
import HolidaysServices from '../services/holidays.service';
import { refundStatusColorsMixin } from '../mixins/refundStatusColorsMixin';
import { downloadRefundPaymentsTemplate } from '../utils/refund-payments-template.utils';
import { can } from '../utils/permissions';

// Sentinel used to decide whether the "Cobertura" chip is shown — a refund whose
// effective supplier is Pawer (the default, no third-party override) shows nothing.
const SUPPLIER_PAWER = 'Pawer';

const REFUND_TABLE_HEADERS = [
  { text: 'Estado', align: 'center', sortable: false, value: 'changelogs' },
  { text: 'Días faltantes', align: 'center', sortable: false, value: 'daysRemaining' },
  { text: 'Fraude', align: 'center', sortable: false, value: 'dongraf', width: '60px' },
  {
    text: 'Solicitud',
    align: 'start',
    sortable: false,
    value: 'referenceIndex'
  },
  { text: 'Empresa', align: 'start', sortable: false, value: 'companyName' },
  { text: 'Plan', align: 'start', sortable: false, value: 'planName' },
  { text: 'Cobertura', align: 'center', sortable: false, value: 'supplier' },
  {
    text: 'RUT/DNI',
    align: 'start',
    sortable: false,
    value: 'user.identifier',
    width: '130px'
  },
  {
    text: 'Usuario',
    align: 'start',
    sortable: false,
    value: 'user'
  },
  { text: 'Mascota', sortable: false, value: 'pet' },
  { text: 'Servicio', align: 'center', sortable: false, value: 'service' },
  { text: 'Fecha de atención', align: 'center', sortable: false, value: 'serviceUsedAt' },
  {
    text: 'Reembolso solicitado',
    align: 'center',
    sortable: false,
    value: 'requestedAmount',
    // Reserves room for the inline editor so the column does not jump.
    width: 180
  },
  {
    text: 'Monto a reembolsar',
    align: 'center',
    sortable: false,
    value: 'approvedAmount',
    width: 180
  },
  { text: 'Actualización', align: 'center', sortable: false, value: 'updated' }
];

export default {
  components: {
    RefundMainteiner,
    Overlay
  },
  mixins: [refundStatusColorsMixin],
  data() {
    return {
      updateChangelogError: false,
      updateChangelogErrorMsg: '',
      updateChangelogOk: false,
      updateChangelogOkMsg: '',
      loadingData: true,
      loadingOverlay: false,
      confirmDialog: false,
      selectedItem: null,
      limit: 30,
      page: 1,
      totalItems: 0,
      sort: [],
      // `search` es el filtro aplicado (el que dispara la consulta);
      // `pendingSearch` es lo que el usuario va marcando en los chips.
      search: [0, 1, 2],
      pendingSearch: [0, 1, 2],
      searchRefund: '',
      // N° de reembolso a abrir al llegar con ?refund= (desde la 360); se limpia al abrirlo.
      autoOpenRefund: null,
      options: {},
      orderBy: 'updatedAt',
      orderDirection: 'desc',
      editingCell: null, // { id, field, value }
      savingCell: false,
      // Días faltantes: plazo en días hábiles desde el último cambio de estado
      slaBusinessDays: 5,
      holidays: {}, // { CL: Set<'YYYY-MM-DD'>, PE: Set<'YYYY-MM-DD'> }
      // Cargar pagos
      loadingPayments: false,
      paymentIntroDialog: false,
      paymentDialog: false,
      parsedPayments: [], // [{ refund, date }]
      paymentParseError: '',
      submittingPayments: false,
      paymentResults: null, // resultado tras confirmar
      paymentSuccess: false,
      paymentSuccessMsg: '',
      paymentError: false,
      paymentErrorMsg: '',
      // Nómina de pagos
      loadingPayroll: false,
      payrollDialog: false,
      /** Solo Chile tiene dos formatos (Bice / Global66); true muestra esa sub-elección. */
      showClFormatChoice: false,
      payroll: null, // { country, format, filename, content, included, excluded }
      payrollPreviewLimit: 15,
      // El color NO se define aquí: viene de la BD via refundStatusColorsMixin.
      // `value` es el índice histórico que espera RefundServices.getRefunds.
      states: [
        { text: 'Registrado', value: 0, sequence: 0, icon: 'mdi-clock-time-nine-outline' },
        { text: 'En revisión', value: 100, sequence: 100, icon: 'mdi-clock-time-nine-outline' },
        { text: 'Por corregir', value: 200, sequence: 200, icon: 'mdi-account-edit' },
        { text: 'Rechazado', value: 300, sequence: 300, icon: 'mdi-close-thick' },
        { text: 'Aprobado', value: 400, sequence: 400, icon: 'mdi-check-bold' },
        { text: 'Pagado', value: 5, sequence: 500, icon: 'mdi-cash-check' },
        { text: 'Pago inválido', value: 600, sequence: 600, icon: 'mdi-cash-remove' }
      ],

      // Abrir el detalle (y el ?refund= de un enlace directo) exige `refund-inbox:view-detail`.
      canSeeDetail: can('refund-inbox:view-detail'),
      canSeePersonalData: can('refund-inbox:view-personal-data'),
      canSeeAmounts: can('refund-inbox:view-amounts'),
      canSeeFraud: can('refund-inbox:view-fraud'),
      refundsRequests: [],
      userId: null,
      petId: null,
      serviceId: null,
      // Feature 049/012: filtro por contraparte real (Pawer / Pacífico / BCI / ...).
      supplierFilter: null,
      // Catálogo completo (todos los refunds, no solo la página cargada).
      availableSuppliers: [],
      loadingSuppliers: false
    };
  },
  computed: {
    /**
     * Columnas visibles: el RUT/DNI, los montos y la columna de fraude tienen su
     * propio permiso, y el backend las envía vacías sin él.
     */
    headers() {
      const hiddenColumns = new Set([
        ...(this.canSeePersonalData ? [] : ['user.identifier']),
        ...(this.canSeeAmounts ? [] : ['requestedAmount', 'approvedAmount']),
        ...(this.canSeeFraud ? [] : ['dongraf'])
      ]);
      return REFUND_TABLE_HEADERS.filter(header => !hiddenColumns.has(header.value));
    },
    searchLabel() {
      return this.canSeePersonalData
        ? 'Buscar nº de solicitud, RUT/DNI o nombre'
        : 'Buscar nº de solicitud o nombre';
    },
    /** True cuando los chips marcados difieren del filtro ya aplicado. */
    hasPendingFilterChanges() {
      const applied = [...this.search].sort().join(',');
      const pending = [...this.pendingSearch].sort().join(',');
      return applied !== pending;
    },

    /**
     * Cabeceras de la vista previa. El CSV real se envía sin cabeceras; las
     * tres columnas "Fijo" son constantes que exige el formato del banco.
     */
    payrollCsvHeaders() {
      return [
        'N° Reembolso',
        'Email usuario',
        'Titular',
        'RUT',
        'Cuenta',
        'Monto',
        'Banco',
        'Tipo cta.',
        'Fijo',
        'Fijo',
        'Fijo',
        'Email'
      ];
    },

    /**
     * Primeras filas del CSV, ya separadas en celdas, para revisar el formato
     * antes de descargar.
     */
    payrollPreviewRows() {
      if (!this.payroll || this.payroll.format !== 'csv' || !this.payroll.content) return [];
      return this.payroll.content
        .split(/\r?\n/)
        .filter(line => line.trim() !== '')
        .slice(0, this.payrollPreviewLimit)
        .map(line => line.split(','));
    },

    /** Cabeceras de la vista previa de Global66, en el orden de sus columnas del Excel. */
    payrollGlobal66Headers() {
      return [
        'País',
        'Tipo receptor',
        'Nombre',
        'Apellido',
        'Tipo doc.',
        'N° doc.',
        'Tipo cuenta',
        'N° cuenta',
        'Banco',
        'Modalidad',
        'Monto'
      ];
    },

    /**
     * Primeras filas del Global66, ya en el mismo orden que sus columnas del
     * Excel, para revisar el formato antes de descargar.
     */
    payrollGlobal66PreviewRows() {
      if (!this.payroll || this.payroll.format !== 'global66' || !this.payroll.rows) return [];
      return this.payroll.rows.slice(0, this.payrollPreviewLimit).map((row) => [
        row.country,
        row.recipientType,
        row.name,
        row.surname,
        row.documentType,
        row.documentNumber,
        row.accountTypeCell,
        row.accountNumber,
        row.bankCell,
        row.modality,
        row.amount
      ]);
    },

    /**
     * Agrupa parsedPayments por fecha → Opción 1:
     * { data: [{ fecha, refunds: [] }, ...] }
     */
    parsedPaymentGroups() {
      const map = {};
      for (const p of this.parsedPayments) {
        if (!map[p.date]) map[p.date] = [];
        map[p.date].push(p.refund);
      }
      return Object.entries(map).map(([fecha, refunds]) => ({ fecha, refunds }));
    },

    /**
     * Agrupa paymentResults.notApproved por estado actual,
     * retornando [{ label, refunds[] }] para mostrar en el template.
     */
    notApprovedGrouped() {
      if (!this.paymentResults?.notApproved?.length) return [];
      const STATUS_LABELS = {
        0: 'Registrado',
        100: 'En revisión',
        200: 'Por modificar',
        300: 'Rechazado',
        600: 'Pago inválido',
        [-1]: 'Sin estado registrado'
      };
      const groups = {};
      for (const item of this.paymentResults.notApproved) {
        const label = STATUS_LABELS[item.statusOrder] ?? `Estado ${item.statusOrder}`;
        if (!groups[label]) groups[label] = [];
        groups[label].push(item.refundNum);
      }
      return Object.entries(groups).map(([label, refunds]) => ({ label, refunds }));
    }
  },
  mounted() {},
  created() {
    this.loadRefundStatusColors();
    this.applyRefundDeepLink();
    this.getRefunds(this.page, this.limit, this.searchRefund);
    this.loadHolidays();
    this.loadSuppliers();
  },
  watch: {
    options: {
      handler() {
        this.page = this.options.page;
        this.limit = this.options.itemsPerPage;
        this.getRefunds(this.page, this.limit, this.searchRefund);
      },
      deep: true
    },

    search: {
      handler() {
        this.page = 1;
        this.sort = [];
        this.getRefunds(this.page, this.limit, this.searchRefund);
      },
      deep: true
    }
  },
  methods: {
    can,
    /** True cuando el servicio de este reembolso lo cubre un tercero, no Pawer. */
    isThirdPartySupplier(item) {
      return Boolean(item.supplier && item.supplier.name && item.supplier.name !== SUPPLIER_PAWER);
    },
    dongrafColor(dongraf) {
      const map = { low: '#22C55E', medium: '#F59E0B', high: '#EF4444', unprocessed: '#9CA3AF' };
      return map[dongraf?.fraudStatus] ?? '#9CA3AF';
    },
    dongrafTooltip(dongraf) {
      if (!dongraf || dongraf.fraudStatus === 'unprocessed') return 'Sin análisis';
      const label = { low: 'Bajo', medium: 'Medio', high: 'Alto' }[dongraf.fraudStatus] ?? '-';
      return `Riesgo ${label} — Score: ${dongraf.score ?? '-'}`;
    },
    preventDecimal(event) {
      if (event.key === '.' || event.key === ',') event.preventDefault();
    },
    /** Símbolo y decimales del país del reembolso (spec 084: suma México). */
    countryMoneyOf(item) {
      return CurrencyUtils.countryMoney(item.countryIso);
    },
    onAmountKeydown(event, item) {
      if (this.countryMoneyOf(item).decimals === 0) this.preventDecimal(event);
    },
    formatAmount(item, field) {
      const value = item[field];
      if (value == null || isNaN(Number(value)) || value === '') return '—';
      return CurrencyUtils.formatCountryAmount(value, item.countryIso);
    },
    startEdit(item, field) {
      let value = item[field];
      if (field === 'serviceUsedAt' && value) {
        // Convertir DD/MM/YYYY → YYYY-MM-DD para el input type="date"
        const parts = value.split('/');
        if (parts.length === 3) value = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      this.editingCell = { id: item.id, field, value: value ?? '' };
    },
    /**
     * Máximo aprobable del reembolso: el menor entre el tope por evento y el
     * monto disponible del servicio. Null cuando no aplica ninguno.
     * `isAvailable` indica que el límite es el monto disponible (para el aviso).
     */
    getRefundAmountCap(item) {
      const perEvent = Number(item.maxAmountPerEventValue);
      const perEventCap = perEvent > 0 ? perEvent : null;
      const available =
        item.availableAmountValue === null || item.availableAmountValue === undefined
          ? null
          : Number(item.availableAmountValue);
      if (available !== null && !isNaN(available) && (perEventCap === null || available < perEventCap)) {
        return { value: available, isAvailable: true };
      }
      return perEventCap === null ? null : { value: perEventCap, isAvailable: false };
    },
    /**
     * El monto a reembolsar no puede superar el tope por evento ni el monto
     * disponible. Se valida antes de abrir el guardado para dejar la celda en
     * edición y que el usuario pueda corregir el valor.
     */
    isWithinRefundAmountCap(item, amount) {
      const cap = this.getRefundAmountCap(item);
      if (!cap || isNaN(amount)) return true;
      return amount <= cap.value;
    },
    refundAmountCapMessage(item) {
      const cap = this.getRefundAmountCap(item);
      const formatted = this.formatAmount({ countryIso: item.countryIso, value: cap.value }, 'value');
      return cap.isAvailable
        ? `A este servicio le quedan ${formatted} disponibles, así que no puedes aprobar un monto mayor. Ingresa ${formatted} o menos.`
        : `Este servicio cubre como máximo ${formatted} por evento, así que no puedes aprobar un monto mayor. Ingresa ${formatted} o menos.`;
    },
    async saveQuickEdit(item) {
      if (!this.editingCell) return;
      if (
        this.editingCell.field === 'approvedAmount' &&
        !this.isWithinRefundAmountCap(item, parseFloat(this.editingCell.value))
      ) {
        this.paymentErrorMsg = this.refundAmountCapMessage(item);
        this.paymentError = true;
        return;
      }
      this.savingCell = true;
      // Se guarda el valor previo: la tabla se actualiza de forma optimista y hay
      // que revertirla si el backend rechaza el cambio.
      const previousValue = item[this.editingCell.field];
      const editedField = this.editingCell.field;
      try {
        const payload = {};
        if (this.editingCell.field === 'serviceUsedAt') {
          payload.serviceUsedAt = this.editingCell.value; // YYYY-MM-DD
          // Actualizar display local DD/MM/YYYY
          const parts = this.editingCell.value.split('-');
          item.serviceUsedAt =
            parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : this.editingCell.value;
        } else if (this.editingCell.field === 'requestedAmount') {
          payload.requestedAmount = parseFloat(this.editingCell.value);
          item.requestedAmount = payload.requestedAmount;
        } else if (this.editingCell.field === 'approvedAmount') {
          payload.approvedAmount = parseFloat(this.editingCell.value);
          item.approvedAmount = payload.approvedAmount;
        }
        await RefundServices.quickUpdate(item.id, payload);
      } catch (e) {
        item[editedField] = previousValue;
        this.paymentErrorMsg = refundErrorMessage(e, 'No se pudo guardar el cambio.');
        this.paymentError = true;
      } finally {
        this.editingCell = null;
        this.savingCell = false;
      }
    },
    async loadHolidays() {
      const year = new Date().getFullYear();
      const years = [year - 1, year, year + 1];
      const countries = ['CL', 'PE', 'MX'];
      const holidays = {};
      await Promise.all(
        countries.map(async country => {
          const set = new Set();
          const results = await Promise.all(
            years.map(y => HolidaysServices.getHolidays(y, country))
          );
          results.forEach(res => {
            if (res.status === 'ok') {
              res.data.forEach(d => set.add(d));
            }
          });
          holidays[country] = set;
        })
      );
      this.holidays = holidays;
    },
    /**
     * Catálogo completo de coberturas para el filtro — todas las que existan en
     * algún refund alguna vez, no solo las de la página cargada.
     */
    async loadSuppliers() {
      this.loadingSuppliers = true;
      const res = await RefundServices.getSuppliers();
      if (res.status === 'ok') this.availableSuppliers = res.data;
      this.loadingSuppliers = false;
    },
    /**
     * Días hábiles faltantes para el plazo. Solo se muestra para reembolsos en
     * estado "Registrado" (0) y "En revisión" (100); en otros estados retorna null.
     * Siempre se calcula desde la fecha del último estado "Registrado"
     * (item.registeredAt): para "En revisión" corresponde a la etapa anterior.
     */
    getDaysRemaining(item) {
      const order =
        item.changelogs && item.changelogs.status ? item.changelogs.status.sequenceOrder : null;
      if (order !== 0 && order !== 100) {
        return null;
      }
      if (!item.registeredAt) {
        return null;
      }
      const country = item.countryIso || 'CL';
      const holidaySet = this.holidays[country] || null;
      return BusinessDaysUtils.remainingBusinessDays(
        new Date(item.registeredAt),
        this.slaBusinessDays,
        holidaySet
      );
    },
    getDaysRemainingColor(days) {
      if (days <= 0) {
        return '#B71C1C'; // rojo intenso - en la fecha o vencido
      }
      if (days <= 2) {
        return '#E53935'; // rojo - cerca de la fecha
      }
      return '#FB8C00'; // naranjo - dentro del plazo
    },
    getMembershipColor(status) {
      switch (status) {
        case 2:
          return '#1CA762'; // Activo - verde
        case 4:
          return '#6A7682'; // Desactivado - gris
        case 0:
        case 5:
          return '#CC7A00'; // Pendiente de pago - amarillo
        default:
          return '#1CA762'; // Verde por defecto
      }
    },
    toggleSort(column) {
      if (this.orderBy === column) {
        // Si es la misma columna, alternar entre asc y desc
        this.orderDirection = this.orderDirection === 'desc' ? 'asc' : 'desc';
      } else {
        // Si es una columna nueva, establecer desc por defecto
        this.orderBy = column;
        this.orderDirection = 'desc';
      }
      this.page = 1;
      this.getRefunds(this.page, this.limit, this.searchRefund);
    },
    // Enlace directo a un reembolso: lo busca por su N° en todos los estados (un pagado o
    // rechazado no está en el filtro por defecto) y marca su ficha para abrirla.
    applyRefundDeepLink() {
      const refund = this.$route.query.refund;
      if (!refund) return;
      this.searchRefund = String(refund);
      this.search = [...ALL_REFUND_STATES];
      this.pendingSearch = [...ALL_REFUND_STATES];
      this.autoOpenRefund = String(refund);
    },
    searchFn() {
      this.getRefunds(this.page, this.limit, this.searchRefund);
    },
    onSupplierFilterChange() {
      this.page = 1;
      this.getRefunds(this.page, this.limit, this.searchRefund);
    },
    parseDate(date) {
      return DateUtils.formatToChileanDate(date);
    },
    /** Vuelve a cargar la bandeja manteniendo los filtros aplicados, incluida la búsqueda. */
    refresh() {
      this.updateChangelogOk = false;
      this.updateChangelogOkMsg = '';

      this.getRefunds(this.page, this.limit, this.searchRefund);
    },
    async getRefunds(page, limit, searchRefund) {
      try {
        this.loadingData = true;
        this.loadingOverlay = true;
        const res = await RefundServices.getRefunds(
          page,
          limit,
          this.search,
          searchRefund,
          this.orderBy,
          this.orderDirection,
          null,
          this.supplierFilter
        );
        if (res.status === 'ok') {
          this.refundsRequests = res.data.result;
          this.totalItems = res.data.totalItems;
        }
        this.loadingData = false;
        this.loadingOverlay = false;
      } catch (err) {
        this.loadingData = false;
        this.loadingOverlay = false;
        console.error(err);
      }
    },
    // ── Nómina de pagos ───────────────────────────────────────
    /** Abre el diálogo en el paso de elección de país. */
    openPayrollDialog() {
      this.payroll = null;
      this.showClFormatChoice = false;
      this.payrollDialog = true;
    },

    closePayrollDialog() {
      this.payrollDialog = false;
      this.payroll = null;
      this.showClFormatChoice = false;
    },

    /**
     * Pide al backend la nómina de los reembolsos aprobados (no pagados).
     * Perú, el Bice de Chile y el Global66 de Chile devuelven JSON y el
     * diálogo pasa al paso de resultado (`this.payroll`) para mostrar una
     * vista previa antes de descargar el archivo definitivo.
     *
     * @param country - 'CL' o 'PE'.
     * @param clFormat - Solo para 'CL': 'bice' (CSV) o 'global66' (Excel).
     */
    async generatePayroll(country, clFormat) {
      this.loadingPayroll = true;
      try {
        if (country === 'CL' && clFormat === 'global66') {
          await this.generateGlobal66Preview();
          return;
        }

        const res = await RefundServices.getPayroll(country);
        if (res.status !== 'ok') {
          this.paymentErrorMsg = res.message || 'No se pudo generar la nómina.';
          this.paymentError = true;
          return;
        }
        this.payroll = res.data;
      } finally {
        this.loadingPayroll = false;
      }
    },

    /** Trae la vista previa de la nómina Global66 y muestra el paso de resultado. */
    async generateGlobal66Preview() {
      const res = await RefundServices.getGlobal66Preview();
      if (res.status !== 'ok') {
        this.paymentErrorMsg = res.message || 'No se pudo generar la nómina.';
        this.paymentError = true;
        return;
      }

      this.payroll = {
        country: 'CL',
        format: 'global66',
        filename: 'nomina-transferencias-global66-cl.xlsm',
        rows: res.data.rows,
        included: res.data.included,
        excluded: res.data.excluded
      };
    },

    /** Descarga el .xlsm de Global66 una vez confirmada la vista previa. */
    async downloadGlobal66Excel() {
      this.loadingPayroll = true;
      try {
        const res = await RefundServices.getGlobal66Payroll();
        if (res.status !== 'ok') {
          this.paymentErrorMsg = res.message || 'No se pudo generar la nómina.';
          this.paymentError = true;
          return;
        }

        this.downloadBlob(res.blob, this.payroll.filename || 'nomina-transferencias-global66-cl.xlsm');

        this.paymentSuccessMsg = res.excludedCount
          ? `Nómina Global66 descargada. ${res.excludedCount} reembolso(s) excluido(s) por datos incompletos.`
          : 'Nómina Global66 descargada.';
        this.paymentSuccess = true;
        this.closePayrollDialog();
      } finally {
        this.loadingPayroll = false;
      }
    },

    downloadPayrollCsv() {
      // BlobPart en UTF-8 sin BOM: el archivo lo consume el banco, no Excel.
      const blob = new Blob([this.payroll.content], { type: 'text/csv;charset=utf-8;' });
      this.downloadBlob(blob, this.payroll.filename || 'nomina-reembolsos.csv');
    },

    /** Descarga un Blob ya armado con un nombre de archivo dado. */
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    async copyPayrollToClipboard() {
      try {
        await navigator.clipboard.writeText(this.payroll.content);
        this.paymentSuccessMsg = 'Nómina copiada al portapapeles.';
        this.paymentSuccess = true;
      } catch (err) {
        console.error('Error copiando la nómina:', err);
        this.paymentErrorMsg = 'No se pudo copiar: selecciona el texto y cópialo manualmente.';
        this.paymentError = true;
      }
    },

    // ── Cargar pagos ──────────────────────────────────────────
    async downloadPaymentsTemplate() {
      try {
        await downloadRefundPaymentsTemplate();
      } catch (err) {
        this.paymentErrorMsg = 'No se pudo generar la plantilla. Intenta nuevamente.';
        this.paymentError = true;
      }
    },

    triggerFileUpload() {
      this.paymentResults = null;
      this.$refs.paymentFileInput.value = '';
      this.$refs.paymentFileInput.click();
    },

    async handlePaymentFile(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.loadingPayments = true;
      this.paymentParseError = '';
      this.parsedPayments = [];
      try {
        this.parsedPayments = await this.parsePaymentFile(file);
        this.paymentIntroDialog = false;
        this.paymentDialog = true;
      } catch (err) {
        this.paymentErrorMsg = err.message || 'Error al leer el archivo.';
        this.paymentError = true;
      } finally {
        this.loadingPayments = false;
      }
    },

    parsePaymentFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => {
          try {
            const workbook = XLSX.read(e.target.result, { type: 'array', cellDates: true });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

            if (rows.length < 2) {
              return reject(new Error('El archivo está vacío o no tiene filas de datos.'));
            }

            // Detectar columnas automáticamente (case-insensitive)
            const headers = rows[0].map(h =>
              String(h || '')
                .toLowerCase()
                .trim()
            );

            const REFUND_KEYS = [
              'reembolsos',
              'reembolso',
              'numero',
              'número',
              'nro',
              'n°',
              'solicitud',
              'folio',
              'id',
              'referencia',
              'index'
            ];
            const DATE_KEYS = ['fecha pago', 'fecha', 'date', 'pago'];

            let refundIdx = headers.findIndex(h => REFUND_KEYS.some(k => h.includes(k)));
            let dateIdx = headers.findIndex(h => DATE_KEYS.some(k => h.includes(k)));

            if (refundIdx === -1 || dateIdx === -1) {
              return reject(
                new Error(
                  `No se detectaron las columnas automáticamente.\n` +
                    `Columnas encontradas: [${rows[0].join(', ')}].\n` +
                    `El archivo debe tener una columna con "número/solicitud/reembolso" y otra con "fecha/pago".`
                )
              );
            }

            const results = [];
            for (let i = 1; i < rows.length; i++) {
              const row = rows[i];
              const rawRefund = row[refundIdx];
              const rawDate = row[dateIdx];
              if (rawRefund == null && rawDate == null) continue; // fila vacía
              results.push({
                refund: String(rawRefund ?? '').trim(),
                date: this.normalizeExcelDate(rawDate)
              });
            }

            if (!results.length) {
              return reject(new Error('No se encontraron filas con datos.'));
            }

            resolve(results);
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
        reader.readAsArrayBuffer(file);
      });
    },

    normalizeExcelDate(value) {
      if (value == null) return '';
      // Con cellDates:true, xlsx convierte seriales a objetos Date
      if (value instanceof Date) {
        if (isNaN(value.getTime())) return '';
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
      const str = String(value).trim();
      // Ya está en YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
      // DD/MM/YYYY  o  DD-MM-YYYY
      // DD/MM/YYYY, DD-MM-YYYY o DD.MM.YYYY (los mismos formatos que la carga de Facturación)
      const m1 = str.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
      if (m1) return `${m1[3]}-${m1[2].padStart(2, '0')}-${m1[1].padStart(2, '0')}`;
      // Fallback: devolver vacío para que el backend descarte la fila
      console.warn('[normalizeExcelDate] Unrecognised date format:', str);
      return '';
    },

    async submitPayments() {
      this.submittingPayments = true;
      try {
        const payload = { data: this.parsedPaymentGroups };
        const res = await RefundServices.uploadPayments(payload);
        if (res.status === 'ok') {
          // Pasar al paso 2: mostrar resultados
          this.paymentResults = res.data;
          this.refresh();
        } else {
          this.paymentErrorMsg = 'Error al cargar los pagos. Intenta nuevamente.';
          this.paymentError = true;
        }
      } catch (err) {
        this.paymentErrorMsg = 'Error inesperado al cargar los pagos.';
        this.paymentError = true;
        console.error(err);
      } finally {
        this.submittingPayments = false;
      }
    },

    closePaymentDialog() {
      this.paymentDialog = false;
      this.paymentResults = null;
      this.parsedPayments = [];
      this.paymentParseError = '';
    },
    // ─────────────────────────────────────────────────────────

    /**
     * Aplica los estados marcados. El watcher de `search` es el que dispara la
     * consulta, así que la tabla se recarga una sola vez y no en cada chip.
     */
    applyStatusFilter() {
      this.search = [...this.pendingSearch];
    },

    /**
     * Pulsación larga sobre un chip: lo deja como único seleccionado, o
     * invierte la selección si ya era el único. Trabaja con el índice del chip
     * porque es lo que guarda v-chip-group. Solo mueve la selección pendiente;
     * sigue requiriendo "Aplicar filtro".
     */
    async longPressChip(index) {
      const isOnlyOneSelected =
        this.pendingSearch.length === 1 && this.pendingSearch[0] === index;

      this.pendingSearch = isOnlyOneSelected
        ? this.states.map((_, i) => i).filter(i => i !== index)
        : [index];
    }
  }
};
</script>

<style scoped lang="scss">
.search-field ::v-deep .v-input__append-inner {
  cursor: pointer;
}

// Inline amount editing inside the data table: the cell is narrow, so the
// native number spinners are dropped to give the digits the whole width.
.amount-edit-cell {
  gap: 4px;
  min-width: 170px;
}

.amount-edit-field {
  width: 112px;
  flex: 0 0 112px;
  font-size: 12px;

  ::v-deep input[type='number'] {
    text-align: right;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  ::v-deep .v-text-field__prefix {
    padding-right: 2px;
    color: #6a7682;
  }
}
</style>
